/* eslint-disable @typescript-eslint/no-explicit-any */
import { useApolloClient } from '@apollo/client'
import { WebPageByHostPathQuery } from '@ts/operations'
import { isEqual } from 'lodash'
import dynamic from 'next/dynamic'
import { ErrorProps } from 'next/error'
import React, { ComponentType } from 'react'

import { ApolloClient, readResults } from '../../lib/algoliaPrefill'

const errorImport: () => any = () => import('next/error')

const ErrorComponent: ComponentType<ErrorProps> = dynamic(errorImport)

const Showcase = dynamic(() => import('../showcase/Showcase'))
const Companies = dynamic(() => import('../companies/Companies'))
const Hero = dynamic(() => import('../hero/Hero'))
const AttendeeSearch = dynamic(() => import('../attendees/AttendeesSearch'))
const ScheduleSearch = dynamic(() => import('../schedule/ScheduleSearch'))

export type ContentItem = NonNullable<
  WebPageByHostPathQuery['webPageByHostPath']
>['content'][number]

export const resultsForPrefill = (item: ContentItem, apolloClient?: ApolloClient) => {
  if (!apolloClient) return undefined

  if (
    item.__typename === 'ComponentWebElementsAttendeeSearchSidebarFilters' ||
    item.__typename === 'ComponentWebElementsCompanySearchSidebarFilters' ||
    item.__typename === 'ComponentWebElementsScheduleSearchSidebarFilters'
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const resultsState = readResults(apolloClient, item)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return resultsState ? { resultsState, searchState: {} } : undefined
  }

  return undefined
}

type ContentElementProps = {
  item: ContentItem
}

const ContentElement = ({ item }: ContentElementProps) => {
  const apolloClient = useApolloClient()

  switch (item.__typename) {
    case 'ComponentWebElementsShowcaseGrid': {
      return <Showcase key={item.id} {...item} />
    }
    case 'ComponentWebElementsScheduleSearchSidebarFilters': {
      return <ScheduleSearch {...item} {...resultsForPrefill(item, apolloClient)} />
    }
    case 'ComponentWebElementsAttendeeSearchSidebarFilters': {
      return <AttendeeSearch key={item.id} {...item} {...resultsForPrefill(item, apolloClient)} />
    }
    case 'ComponentWebElementsCompanySearchSidebarFilters': {
      return <Companies key={item.id} {...item} {...resultsForPrefill(item, apolloClient)} />
    }
    case 'ComponentWebElementsHeroSection': {
      return <Hero key={item.id} {...item} />
    }
    default: {
      return (
        <ErrorComponent
          key={`error_${item.__typename}`}
          statusCode={404}
          title="Content Not Found"
        />
      )
    }
  }
}

export default React.memo(ContentElement, (prev, next) => isEqual(prev.item, next.item))
